// Small fetch wrapper matching BackFriend's src/lib/api.ts shape, adapted to
// this app's auth model: instead of being handed an idToken by the caller,
// it pulls the current one from lib/auth.ts itself, so every page can just
// call apiGet/apiPost/etc. without threading a token through props.
import { getIdToken } from "./auth";

// On our own hosts (and localhost) call the API directly. On a provisioned
// club domain, which is served by the club distribution, go through its
// same-origin /api proxy: the API's CORS list only names our own origins.
export function apiBase(
  host: string,
  apiUrl: string,
  siteHosts: string | undefined,
): string {
  if (!siteHosts) return apiUrl; // local dev / CI builds
  const own = siteHosts.split(",").map((h) => h.trim().toLowerCase()).filter(Boolean);
  const h = host.toLowerCase();
  if (h === "localhost" || h === "127.0.0.1" || own.includes(h)) return apiUrl;
  return "/api";
}

const API_URL = apiBase(
  typeof window === "undefined" ? "" : window.location.hostname,
  import.meta.env.VITE_API_URL as string,
  import.meta.env.VITE_SITE_HOSTS as string | undefined,
);

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await getIdToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(init.headers as Record<string, string> | undefined),
  };
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`${API_URL}${path}`, { ...init, headers });
  if (res.status === 204) return undefined as unknown as T;
  const text = await res.text();
  const json = text ? JSON.parse(text) : undefined;
  if (!res.ok) {
    const msg = (json && json.error) || `HTTP ${res.status}`;
    throw new Error(msg);
  }
  return json as T;
}

export function apiGet<T>(path: string): Promise<T> {
  return request<T>(path, { method: "GET" });
}
export function apiPost<T>(path: string, body?: unknown): Promise<T> {
  return request<T>(path, { method: "POST", body: body !== undefined ? JSON.stringify(body) : undefined });
}
export function apiPut<T>(path: string, body?: unknown): Promise<T> {
  return request<T>(path, { method: "PUT", body: body !== undefined ? JSON.stringify(body) : undefined });
}
export function apiDelete<T>(path: string): Promise<T> {
  return request<T>(path, { method: "DELETE" });
}

// ----- Shared response shapes, mirroring backend/src/lib/db.ts -----

export type CustomFields = Record<string, string | number | boolean>;

export interface PublicConfig {
  name?: string;
  facebookEnabled?: boolean;
  cognitoDomain?: string;
  // Deployment-level feature flags/info can grow here; kept loose since the
  // backend handler for GET /config is still being written in parallel.
  [key: string]: unknown;
}

export interface UserRow {
  userId: string;
  email: string;
  name: string;
  phone?: string;
  /** Text me when my round/race is coming up. Undefined = off. */
  smsRaceReminders?: boolean;
  bio?: string;
  profilePhotoKey?: string;
  sponsors?: string[];
  socialLinks?: Record<string, string>;
  customFields?: CustomFields;
  creditBalanceCents: number;
  isVerifiedMember?: boolean;
  isAdmin?: boolean;
  /** Admin list only -- which sign-in method this user last used, computed
   *  live from Cognito (see backend/src/handlers/admin/listUsers.ts). */
  authProvider?: "email" | "facebook";
  createdAt: string;
  updatedAt: string;
}

export interface VehicleRow {
  userId: string;
  vehicleId: string;
  manufacturer: string;
  class: string;
  radioManufacturer?: string;
  transponderNumber?: string;
  customFields?: CustomFields;
  createdAt: string;
  updatedAt: string;
}

export type EventStatus = "draft" | "published";

/** A racing class offered at an event, plus how its race day is structured.
 *  qualifyingRounds/mains are config only (e.g. "3 qualifiers, 2 mains") --
 *  heats and results are entered by hand in the admin Manage view, not
 *  auto-generated from these counts. */
export interface EventClassConfig {
  name: string;
  qualifyingRounds: number;
  mains: number;
}

export interface EventRow {
  eventId: string;
  /** Owning club. Unset on events created before clubs existed. */
  clubId?: string;
  status: EventStatus;
  title: string;
  description?: string;
  dateISO: string;
  /** Optional -- set when the event spans multiple days or has a known end time. */
  endDateISO?: string;
  location?: string;
  classes?: EventClassConfig[];
  entryFeeCents: number;
  registrationOpensAt?: string;
  registrationClosesAt?: string;
  featured?: boolean;
  featuredOrder?: number;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export type MediaType = "photo" | "video";

export interface MediaRow {
  eventId: string;
  mediaId: string;
  uploaderId: string;
  /** Present on /me/media (your own uploads); the public event gallery
   *  (/events/{eventId}/media) never returns these -- uploader identity
   *  isn't exposed to that public, signed-out-visible listing. */
  uploaderEmail?: string;
  uploaderName?: string;
  s3Key: string;
  type: MediaType;
  caption?: string;
  createdAt: string;
  /** Convenience field a handler may add to save the client a second call;
   *  if absent, build the URL from s3Key + the media CDN origin. */
  url?: string;
  /** /me/media only: the event's title, so drafts don't show a bare id. */
  eventTitle?: string;
}

export type RegistrationStatus = "pending" | "paid" | "refunded" | "cancelled";

export interface RegistrationRow {
  eventId: string;
  registrationId: string;
  racerUserId?: string;
  racerName: string;
  racerClass: string;
  purchaserUserId: string;
  stripeCheckoutSessionId?: string;
  amountCents: number;
  creditsAppliedCents: number;
  /** Checkout couldn't reach Stripe: still pending, credits held until an
   *  admin cancels it or a retry gets a Stripe session. */
  paymentHeldAt?: string;
  creditsReleasedAt?: string;
  status: RegistrationStatus;
  createdAt: string;
  updatedAt: string;
}

/** /me/registrations: each row plus the event and club it's for, since a
 *  racer's registrations span every club on RC Race HQ. */
export interface MyRegistrationRow extends RegistrationRow {
  eventTitle?: string;
  eventDateISO?: string;
  eventEndDateISO?: string;
  clubId?: string;
  clubName?: string;
  clubDomain?: string;
}

/** Public roster shape (GET /events/{eventId}/registrations) -- just enough
 *  to show who's racing and whether they've paid, none of RegistrationRow's
 *  purchaser/amount/Stripe fields. */
export interface EventRegistrationPublicRow {
  racerName: string;
  racerClass: string;
  paid: boolean;
}

export type HeatRoundType = "qualifier" | "main";

export interface HeatResult {
  racerName: string;
  place?: number;
  laps?: number;
  bestLapTime?: string;
}

export interface HeatRow {
  eventId: string;
  heatId: string;
  className: string;
  roundType: HeatRoundType;
  roundNumber: number;
  /** Race day ("YYYY-MM-DD"). Unset on older heats -- shown under day one. */
  dayISO?: string;
  /** Run order within its day. */
  order?: number;
  results: HeatResult[];
  createdAt: string;
  updatedAt: string;
}

export interface CreditLedgerRow {
  userId: string;
  txnId: string;
  amountCents: number;
  reason: string;
  clubId?: string;
  relatedEventId?: string;
  createdBy: string;
  createdAt: string;
}

/** One club's credit balance and history. clubId "unassigned" holds old
 *  credits from before per-club balances that had no club to attach to. */
export interface ClubCredits {
  clubId: string;
  clubName: string;
  clubDomain?: string;
  balanceCents: number;
  ledger: CreditLedgerRow[];
}

export type ArticleStatus = "draft" | "published";

export interface ArticleRow {
  articleId: string;
  /** Set on news a club manager wrote from Manage. */
  clubId?: string;
  /** Position on the club's news page (ascending). */
  order?: number;
  status: ArticleStatus;
  title: string;
  body: string;
  summary?: string;
  publishDate: string;
  featured?: boolean;
  featuredOrder?: number;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export type FieldAppliesTo = "user" | "vehicle";
export type FieldType = "text" | "number" | "boolean" | "select";

export interface FieldDefinitionRow {
  appliesTo: FieldAppliesTo;
  fieldKey: string;
  label: string;
  type: FieldType;
  options?: string[];
  required: boolean;
  createdAt: string;
}

export type LocationType = "track" | "hobby_shop";

export interface LocationRow {
  locationId: string;
  type: LocationType;
  order: number;
  name: string;
  address?: string;
  description?: string;
  phone?: string;
  /** Free-text business hours, one line per day or range. */
  hours?: string;
  website?: string;
  managerEmail?: string;
  photoKeys?: string[];
  /** Computed by the backend from photoKeys -- ready to use as an <img src>. */
  photoUrls?: string[];
  /** Controls public visibility -- undefined counts as published (see the
   *  matching comment on the backend's LocationRow). */
  published?: boolean;
  createdAt: string;
  updatedAt: string;
}

// ----- Public -----
export const getConfig = () => apiGet<PublicConfig>("/config");
export const getLivestreamStatus = (clubId?: string) =>
  apiGet<{ live: boolean }>(clubId ? `/livestream-status?clubId=${encodeURIComponent(clubId)}` : "/livestream-status");
export const listEvents = (clubId?: string) =>
  apiGet<{ events: EventRow[] }>(clubId ? `/events?clubId=${encodeURIComponent(clubId)}` : "/events");
export const getEvent = (eventId: string) => apiGet<{ event: EventRow }>(`/events/${encodeURIComponent(eventId)}`);
export const listEventRegistrationsPublic = (eventId: string) =>
  apiGet<{ registrations: EventRegistrationPublicRow[] }>(`/events/${encodeURIComponent(eventId)}/registrations`);
/** With a clubId: that club's news in its managers' order. Without: site-wide news. */
export const listArticles = (clubId?: string) =>
  apiGet<{ articles: ArticleRow[] }>(clubId ? `/articles?clubId=${encodeURIComponent(clubId)}` : "/articles");
export const getArticle = (articleId: string) =>
  apiGet<{ article: ArticleRow }>(`/articles/${encodeURIComponent(articleId)}`);
/** With a clubId: that club's rows (e.g. its hobby shops). Without: site-wide rows. */
export const listLocations = (type: LocationType, clubId?: string) =>
  apiGet<{ locations: LocationRow[] }>(`/locations?${new URLSearchParams(clubId ? { type, clubId } : { type })}`);

// ----- Me (authenticated) -----
export const getMe = () => apiGet<{ user: UserRow; managedClubCount?: number }>("/me");
export const updateMe = (body: Partial<UserRow>) => apiPut<{ user: UserRow }>("/me", body);

// Member-readable field metadata (any signed-in user) -- lets the vehicle/
// profile forms render admin-defined fields, including "select" dropdowns,
// without needing admin privileges just to read the field list.
export const listFields = (appliesTo: FieldAppliesTo) =>
  apiGet<{ fields: FieldDefinitionRow[] }>(`/fields?${new URLSearchParams({ appliesTo })}`);

export const listMyVehicles = () => apiGet<{ vehicles: VehicleRow[] }>("/me/vehicles");
export const createVehicle = (body: Partial<VehicleRow>) => apiPost<{ vehicle: VehicleRow }>("/me/vehicles", body);
export const updateVehicle = (vehicleId: string, body: Partial<VehicleRow>) =>
  apiPut<{ vehicle: VehicleRow }>(`/me/vehicles/${encodeURIComponent(vehicleId)}`, body);
export const deleteVehicle = (vehicleId: string) =>
  apiDelete<{ ok: boolean }>(`/me/vehicles/${encodeURIComponent(vehicleId)}`);

export const listMyMedia = (clubId?: string) =>
  apiGet<{ media: MediaRow[] }>(clubId ? `/me/media?clubId=${encodeURIComponent(clubId)}` : "/me/media");
export const listMyRegistrations = () => apiGet<{ registrations: MyRegistrationRow[] }>("/me/registrations");
/** Credits are per club; with eventId, spendableCents is the balance at
 *  that event's club (the only credits its checkout can use). */
export const getMyCredits = (eventId?: string) =>
  apiGet<{ clubs: ClubCredits[]; spendableCents?: number }>(
    eventId ? `/me/credits?eventId=${encodeURIComponent(eventId)}` : "/me/credits",
  );

// ----- Event media (any signed-in user; handler enforces verified-member) -----
export const listEventMedia = (eventId: string) =>
  apiGet<{ media: MediaRow[] }>(`/events/${encodeURIComponent(eventId)}/media`);

// The backend's Body shape is fileName (camelCase N) -- keep the key
// explicit here rather than an object-shorthand { filename } that would
// silently send the wrong JSON key and 400 on every upload.
export const getMediaUploadUrl = (eventId: string, filename: string, contentType: string) =>
  apiPost<{ uploadUrl: string; mediaId: string; s3Key: string }>(`/events/${encodeURIComponent(eventId)}/media/upload-url`, {
    fileName: filename,
    contentType,
  });

export const confirmMedia = (
  eventId: string,
  body: { mediaId: string; s3Key: string; type: MediaType; caption?: string },
) => apiPost<{ media: MediaRow }>(`/events/${encodeURIComponent(eventId)}/media`, body);

export const deleteMedia = (eventId: string, mediaId: string) =>
  apiDelete<{ ok: boolean }>(`/events/${encodeURIComponent(eventId)}/media/${encodeURIComponent(mediaId)}`);

// ----- Checkout -----
export interface CheckoutRacer {
  racerName: string;
  racerClass: string;
}
export const createCheckoutSession = (body: {
  eventId: string;
  racers: CheckoutRacer[];
  creditsAppliedCents?: number;
}) =>
  // Stripe unavailable -> rejects (503) with "Payment provider unavailable,
  // your credits are held; ...". The registrations and credits are kept, and
  // retrying the same cart reuses them instead of taking credits again.
  apiPost<{ checkoutUrl?: string; freeCheckout?: boolean }>("/checkout", body);

// ----- Admin -----
export const listAdminUsers = (q?: string) => {
  const qs = q ? `?${new URLSearchParams({ q })}` : "";
  return apiGet<{ users: UserRow[] }>(`/admin/users${qs}`);
};
export const updateAdminUser = (userId: string, body: Partial<UserRow>) =>
  apiPut<{ user: UserRow }>(`/admin/users/${encodeURIComponent(userId)}`, body);

export const listFieldDefinitions = (appliesTo: FieldAppliesTo) =>
  apiGet<{ fields: FieldDefinitionRow[] }>(`/admin/fields?${new URLSearchParams({ appliesTo })}`);
export const upsertFieldDefinition = (body: Partial<FieldDefinitionRow>) =>
  apiPost<{ field: FieldDefinitionRow }>("/admin/fields", body);
export const deleteFieldDefinition = (appliesTo: FieldAppliesTo, fieldKey: string) =>
  apiDelete<{ ok: boolean }>(
    `/admin/fields/${encodeURIComponent(appliesTo)}/${encodeURIComponent(fieldKey)}`,
  );

export const listAdminEvents = () => apiGet<{ events: EventRow[] }>("/admin/events");
export const createAdminEvent = (body: Partial<EventRow>) => apiPost<{ event: EventRow }>("/admin/events", body);
export const updateAdminEvent = (eventId: string, body: Partial<EventRow>) =>
  apiPut<{ event: EventRow }>(`/admin/events/${encodeURIComponent(eventId)}`, body);
export const deleteAdminEvent = (eventId: string) =>
  apiDelete<{ ok: boolean }>(`/admin/events/${encodeURIComponent(eventId)}`);

export const listEventRegistrations = (eventId: string) =>
  apiGet<{ registrations: RegistrationRow[] }>(`/admin/events/${encodeURIComponent(eventId)}/registrations`);

export const updateRegistrationStatus = (eventId: string, registrationId: string, status: RegistrationStatus) =>
  apiPut<{ registration: RegistrationRow; creditsReleasedCents?: number }>(
    `/admin/events/${encodeURIComponent(eventId)}/registrations/${encodeURIComponent(registrationId)}`,
    { status },
  );

// Admin-initiated registration -- creates a real Stripe Checkout Session for
// the given member (they still pay via the returned link; this doesn't
// charge the admin or mark it paid for free). See
// backend/src/lib/checkout.ts for the shared logic with the self-service
// /checkout route.
export const createAdminRegistrationCheckout = (
  eventId: string,
  body: { userId: string; racerName: string; racerClass: string },
) =>
  apiPost<{ freeCheckout?: true; checkoutUrl?: string; registrationIds: string[] }>(
    `/admin/events/${encodeURIComponent(eventId)}/registrations`,
    body,
  );

export const listHeats = (eventId: string) =>
  apiGet<{ heats: HeatRow[] }>(`/admin/events/${encodeURIComponent(eventId)}/heats`);
export const createHeat = (eventId: string, body: { className: string; roundType: HeatRoundType; roundNumber: number }) =>
  apiPost<HeatRow>(`/admin/events/${encodeURIComponent(eventId)}/heats`, body);
export const updateHeatResults = (eventId: string, heatId: string, results: HeatResult[]) =>
  apiPut<HeatRow>(`/admin/events/${encodeURIComponent(eventId)}/heats/${encodeURIComponent(heatId)}`, { results });
export const deleteHeat = (eventId: string, heatId: string) =>
  apiDelete<{ ok: boolean }>(`/admin/events/${encodeURIComponent(eventId)}/heats/${encodeURIComponent(heatId)}`);

export const awardCredit = (body: { userId: string; clubId: string; amountCents: number; reason: string }) =>
  apiPost<{ clubId: string; newBalanceCents: number }>("/admin/credits/award", body);

export const listAdminArticles = () => apiGet<{ articles: ArticleRow[] }>("/admin/articles");
export const createAdminArticle = (body: Partial<ArticleRow>) =>
  apiPost<{ article: ArticleRow }>("/admin/articles", body);
export const updateAdminArticle = (articleId: string, body: Partial<ArticleRow>) =>
  apiPut<{ article: ArticleRow }>(`/admin/articles/${encodeURIComponent(articleId)}`, body);
export const deleteAdminArticle = (articleId: string) =>
  apiDelete<{ ok: boolean }>(`/admin/articles/${encodeURIComponent(articleId)}`);

export const listAdminLocations = (type?: LocationType) => {
  const qs = type ? `?${new URLSearchParams({ type })}` : "";
  return apiGet<{ locations: LocationRow[] }>(`/admin/locations${qs}`);
};
export const createAdminLocation = (body: Partial<LocationRow>) =>
  apiPost<{ location: LocationRow }>("/admin/locations", body);
export const updateAdminLocation = (locationId: string, body: Partial<LocationRow>) =>
  apiPut<{ location: LocationRow }>(`/admin/locations/${encodeURIComponent(locationId)}`, body);
export const deleteAdminLocation = (locationId: string) =>
  apiDelete<{ ok: boolean }>(`/admin/locations/${encodeURIComponent(locationId)}`);
export const getLocationPhotoUploadUrl = (locationId: string, fileName: string, contentType: string) =>
  apiPost<{ uploadUrl: string; s3Key: string }>(`/admin/locations/${encodeURIComponent(locationId)}/photo-upload-url`, {
    fileName,
    contentType,
  });

// Admin editing of another member's vehicles -- same shapes as the /me/vehicles
// routes, scoped by userId instead of the caller's own sub.
export const createUserVehicle = (userId: string, body: Omit<VehicleRow, "userId" | "vehicleId" | "createdAt" | "updatedAt">) =>
  apiPost<VehicleRow>(`/admin/users/${encodeURIComponent(userId)}/vehicles`, body);
export const listUserVehicles = (userId: string) =>
  apiGet<{ vehicles: VehicleRow[] }>(`/admin/users/${encodeURIComponent(userId)}/vehicles`);
export const updateUserVehicle = (userId: string, vehicleId: string, body: Partial<VehicleRow>) =>
  apiPut<VehicleRow>(`/admin/users/${encodeURIComponent(userId)}/vehicles/${encodeURIComponent(vehicleId)}`, body);
export const deleteUserVehicle = (userId: string, vehicleId: string) =>
  apiDelete<{ ok: boolean }>(`/admin/users/${encodeURIComponent(userId)}/vehicles/${encodeURIComponent(vehicleId)}`);

// ----- Clubs -----

export type ClubRole = "owner" | "manager";

export interface ClubManagerRow {
  userId: string;
  clubId: string;
  email: string;
  /** Missing on rows from before roles existed -- treat as "manager". */
  role?: ClubRole;
  createdAt: string;
}

export interface ClubRow {
  clubId: string;
  name: string;
  /** Hostnames serving this club's site; the first is its primary domain. */
  domains?: string[];
  /** Last Test-button result per domain. */
  domainChecks?: Record<string, DomainCheckResult>;
  /** CloudFront/certificate state per provisioned domain (admin list). */
  domainProvisioning?: Record<string, DomainProvision>;
  /** DNS records the club needs to add, per provisioned domain (admin list). */
  domainRecords?: Record<string, DnsRecord[]>;
  /** Public contact details (admin list). */
  contact?: ClubContact;
  /** Facebook sign-in on the club's domains; missing = on (admin list). */
  facebookSignIn?: boolean;
  createdAt: string;
  updatedAt: string;
  /** Admin list only. */
  managers?: ClubManagerRow[];
  /** Manage list only: the caller's role in this club. */
  myRole?: "admin" | ClubRole;
}

/** The club whose domain this site is being served on (null on the
 *  RC Race HQ service site). */
export interface StreamEmbed {
  label: string;
  url: string;
}

/** A club's livestream settings (Manage -> Streaming). */
export interface ClubStreamSettings {
  /** The club's LiveRC watch page; drives the nav's live indicator. */
  liveRcUrl?: string;
  /** Players shown on the club's /live page, in order. */
  embeds: StreamEmbed[];
}

/** A club's public contact details (Admin -> Clubs, Manage -> Advanced
 *  settings). Plain text -- always render as text, never HTML. */
export interface ClubContact {
  /** Where the About page's contact form delivers; also a mailto link. */
  email?: string;
  /** E.164, e.g. "+15095551234". */
  phone?: string;
  /** Shown on the About page with its line breaks. */
  about?: string;
  /** Free-text city or address. */
  location?: string;
}

export interface SiteClub {
  clubId: string;
  name: string;
  domains: string[];
  stream?: ClubStreamSettings;
  /** Manage -> Advanced settings custom CSS ("" = default look). */
  customCss?: string;
  contact?: ClubContact;
  /** False hides "Sign in with Facebook" on this club's site. */
  facebookSignIn?: boolean;
}
export const getClubByHost = (host: string) =>
  apiGet<{ club: SiteClub | null }>(`/clubs/by-host?host=${encodeURIComponent(host)}`);

export const listAdminClubs = () =>
  apiGet<{ clubs: ClubRow[]; cnameTarget?: string; canProvision?: boolean }>("/admin/clubs");

export type ProvisionStatus = "validating" | "deploying" | "active" | "failed" | "removing";
export interface DomainProvision {
  status: ProvisionStatus;
  message: string;
  requestedAt: string;
  updatedAt: string;
}
export interface DnsRecord {
  purpose: "site" | "certificate";
  name: string;
  type: "CNAME" | "ALIAS";
  value: string;
  /** Already added to our own Route 53 zone: nothing for the club to do. */
  addedForYou?: boolean;
}
type ProvisionResponse = { domain: string; provision: DomainProvision | null; records: DnsRecord[] };
export const provisionClubDomain = (clubId: string, domain: string) =>
  apiPost<ProvisionResponse>(`/admin/clubs/${encodeURIComponent(clubId)}/domains/provision`, { domain });
export const deprovisionClubDomain = (clubId: string, domain: string) =>
  apiDelete<ProvisionResponse>(
    `/admin/clubs/${encodeURIComponent(clubId)}/domains/provision?domain=${encodeURIComponent(domain)}`,
  );
export const createAdminClub = (name: string, contact?: ClubContact) =>
  apiPost<{ club: ClubRow }>("/admin/clubs", { name, contact });
export const updateAdminClub = (
  clubId: string,
  body: { name?: string; domains?: string[] | string; contact?: ClubContact; facebookSignIn?: boolean },
) =>
  apiPut<{ club: ClubRow }>(`/admin/clubs/${encodeURIComponent(clubId)}`, body);
export const addClubManager = (clubId: string, email: string, role: ClubRole = "manager") =>
  apiPost<{ manager: ClubManagerRow }>(`/admin/clubs/${encodeURIComponent(clubId)}/managers`, { email, role });
export const removeClubManager = (clubId: string, userId: string) =>
  apiDelete<void>(`/admin/clubs/${encodeURIComponent(clubId)}/managers/${encodeURIComponent(userId)}`);
export interface DomainCheckStep {
  step: "dns" | "https" | "app";
  status: "pass" | "warn" | "fail" | "skipped";
  message: string;
}
export interface DomainCheckResult {
  domain: string;
  ok: boolean;
  checkedAt: string;
  steps: DomainCheckStep[];
}
export const testClubDomain = (clubId: string, domain: string) =>
  apiPost<{ result: DomainCheckResult }>(`/manage/clubs/${encodeURIComponent(clubId)}/domains/test`, { domain });
export const adoptClubEvents = (clubId: string) =>
  apiPost<{ adopted: number }>(`/admin/clubs/${encodeURIComponent(clubId)}/adopt-events`);

// ----- Manage (club/event managers) -----

export const listMyClubs = () => apiGet<{ clubs: ClubRow[] }>("/manage/clubs");
export const listClubEvents = (clubId: string) =>
  apiGet<{ club: ClubRow; events: EventRow[] }>(`/manage/clubs/${encodeURIComponent(clubId)}/events`);
export const listManageClubManagers = (clubId: string) =>
  apiGet<{ managers: ClubManagerRow[] }>(`/manage/clubs/${encodeURIComponent(clubId)}/managers`);
export const addManageClubManager = (clubId: string, email: string, role: ClubRole) =>
  apiPost<{ manager: ClubManagerRow }>(`/manage/clubs/${encodeURIComponent(clubId)}/managers`, { email, role });
export const awardManageClubCredit = (
  clubId: string,
  body: { email: string; amountCents: number; reason: string; relatedEventId?: string },
) =>
  apiPost<{ email: string; clubId: string; newBalanceCents: number }>(
    `/manage/clubs/${encodeURIComponent(clubId)}/credits`,
    body,
  );
export const removeManageClubManager = (clubId: string, userId: string) =>
  apiDelete<void>(`/manage/clubs/${encodeURIComponent(clubId)}/managers/${encodeURIComponent(userId)}`);

const manageBase = (clubId: string) => `/manage/clubs/${encodeURIComponent(clubId)}`;

export interface ManageArticleInput {
  title?: string;
  body?: string;
  summary?: string;
  status?: ArticleStatus;
  publishDate?: string;
}
export const listManageArticles = (clubId: string) =>
  apiGet<{ articles: ArticleRow[] }>(`${manageBase(clubId)}/articles`);
export const createManageArticle = (clubId: string, body: ManageArticleInput) =>
  apiPost<{ article: ArticleRow }>(`${manageBase(clubId)}/articles`, body);
export const updateManageArticle = (clubId: string, articleId: string, body: ManageArticleInput) =>
  apiPut<{ article: ArticleRow }>(`${manageBase(clubId)}/articles/${encodeURIComponent(articleId)}`, body);
export const deleteManageArticle = (clubId: string, articleId: string) =>
  apiDelete<void>(`${manageBase(clubId)}/articles/${encodeURIComponent(articleId)}`);
export const reorderManageArticles = (clubId: string, ids: string[]) =>
  apiPost<{ articles: ArticleRow[] }>(`${manageBase(clubId)}/articles/order`, { ids });
export const adoptManageArticles = (clubId: string) =>
  apiPost<{ adopted: number; articles: ArticleRow[] }>(`${manageBase(clubId)}/articles/adopt`);

export interface HobbyShopInput {
  name?: string;
  address?: string;
  phone?: string;
  hours?: string;
  website?: string;
  published?: boolean;
}
export const listManageHobbyShops = (clubId: string) =>
  apiGet<{ shops: LocationRow[] }>(`${manageBase(clubId)}/hobby-shops`);
export const createManageHobbyShop = (clubId: string, body: HobbyShopInput) =>
  apiPost<{ shop: LocationRow }>(`${manageBase(clubId)}/hobby-shops`, body);
export const updateManageHobbyShop = (clubId: string, locationId: string, body: HobbyShopInput) =>
  apiPut<{ shop: LocationRow }>(`${manageBase(clubId)}/hobby-shops/${encodeURIComponent(locationId)}`, body);
export const deleteManageHobbyShop = (clubId: string, locationId: string) =>
  apiDelete<void>(`${manageBase(clubId)}/hobby-shops/${encodeURIComponent(locationId)}`);
export const reorderManageHobbyShops = (clubId: string, ids: string[]) =>
  apiPost<{ shops: LocationRow[] }>(`${manageBase(clubId)}/hobby-shops/order`, { ids });
export const adoptManageHobbyShops = (clubId: string) =>
  apiPost<{ adopted: number; shops: LocationRow[] }>(`${manageBase(clubId)}/hobby-shops/adopt`);

export interface TrackInput {
  name?: string;
  address?: string;
  description?: string;
  phone?: string;
  website?: string;
  published?: boolean;
}
export const listManageTracks = (clubId: string) =>
  apiGet<{ tracks: LocationRow[] }>(`${manageBase(clubId)}/tracks`);
export const createManageTrack = (clubId: string, body: TrackInput) =>
  apiPost<{ track: LocationRow }>(`${manageBase(clubId)}/tracks`, body);
export const updateManageTrack = (clubId: string, locationId: string, body: TrackInput) =>
  apiPut<{ track: LocationRow }>(`${manageBase(clubId)}/tracks/${encodeURIComponent(locationId)}`, body);
export const deleteManageTrack = (clubId: string, locationId: string) =>
  apiDelete<void>(`${manageBase(clubId)}/tracks/${encodeURIComponent(locationId)}`);
export const reorderManageTracks = (clubId: string, ids: string[]) =>
  apiPost<{ tracks: LocationRow[] }>(`${manageBase(clubId)}/tracks/order`, { ids });
export const adoptManageTracks = (clubId: string) =>
  apiPost<{ adopted: number; tracks: LocationRow[] }>(`${manageBase(clubId)}/tracks/adopt`);

export const getManageStream = (clubId: string) =>
  apiGet<{ stream: ClubStreamSettings }>(`${manageBase(clubId)}/stream`);
export const updateManageStream = (clubId: string, stream: ClubStreamSettings) =>
  apiPut<{ stream: ClubStreamSettings }>(`${manageBase(clubId)}/stream`, stream);

export interface ManageClubCss {
  customCss: string;
  customCssUpdatedAt: string | null;
  customCssUpdatedBy: string | null;
  maxBytes: number;
}
export const getManageClubCss = (clubId: string) => apiGet<ManageClubCss>(`${manageBase(clubId)}/css`);
export const updateManageClubCss = (clubId: string, customCss: string) =>
  apiPut<ManageClubCss>(`${manageBase(clubId)}/css`, { customCss });

export interface ManageClubContact {
  contact: ClubContact;
  limits: { email: number; phone: number; about: number; location: number };
}
export const getManageClubContact = (clubId: string) =>
  apiGet<ManageClubContact>(`${manageBase(clubId)}/contact`);
export const updateManageClubContact = (clubId: string, contact: ClubContact) =>
  apiPut<ManageClubContact>(`${manageBase(clubId)}/contact`, contact);

export interface ManageClubSignIn {
  facebook: boolean;
}
export const getManageClubSignIn = (clubId: string) => apiGet<ManageClubSignIn>(`${manageBase(clubId)}/sign-in`);
export const updateManageClubSignIn = (clubId: string, facebook: boolean) =>
  apiPut<ManageClubSignIn>(`${manageBase(clubId)}/sign-in`, { facebook });

// ----- About page contact form -----

export interface ContactMessageInput {
  /** Omitted on the service site, which mails the platform address. */
  clubId?: string;
  name: string;
  email: string;
  subject: string;
  message: string;
  /** Hidden honeypot -- left blank by people, filled in by bots. */
  website: string;
}
export const sendContactMessage = (body: ContactMessageInput) => apiPost<{ sent: boolean }>("/contact", body);
// ----- Manage → event schedule (heats/mains per day) and Manage Race -----

export interface ManageRegistrationRow {
  registrationId: string;
  racerName: string;
  racerClass: string;
  racerUserId?: string;
  status: RegistrationStatus;
}

export interface ManageHeatInput {
  className?: string;
  roundType?: HeatRoundType;
  roundNumber?: number;
  dayISO?: string;
  order?: number;
}

const manageEventPath = (clubId: string, eventId: string) =>
  `/manage/clubs/${encodeURIComponent(clubId)}/events/${encodeURIComponent(eventId)}`;

export const listManageEventHeats = (clubId: string, eventId: string) =>
  apiGet<{ event: EventRow; heats: HeatRow[] }>(`${manageEventPath(clubId, eventId)}/heats`);
export const createManageEventHeat = (clubId: string, eventId: string, body: ManageHeatInput) =>
  apiPost<{ heat: HeatRow }>(`${manageEventPath(clubId, eventId)}/heats`, body);
export const updateManageEventHeat = (clubId: string, eventId: string, heatId: string, body: ManageHeatInput) =>
  apiPut<{ heat: HeatRow }>(`${manageEventPath(clubId, eventId)}/heats/${encodeURIComponent(heatId)}`, body);
export const deleteManageEventHeat = (clubId: string, eventId: string, heatId: string) =>
  apiDelete<void>(`${manageEventPath(clubId, eventId)}/heats/${encodeURIComponent(heatId)}`);
export const listManageEventRegistrations = (clubId: string, eventId: string) =>
  apiGet<{ registrations: ManageRegistrationRow[] }>(`${manageEventPath(clubId, eventId)}/registrations`);

// ----- Manage → club events (create/edit/delete; clubId comes from the path) -----

/** Event fields a club manager can set -- no clubId or featured/featuredOrder. */
export type ManageEventInput = Partial<
  Pick<
    EventRow,
    | "title"
    | "description"
    | "dateISO"
    | "endDateISO"
    | "location"
    | "classes"
    | "entryFeeCents"
    | "registrationOpensAt"
    | "registrationClosesAt"
    | "status"
  >
>;
export const createManageEvent = (clubId: string, body: ManageEventInput) =>
  apiPost<{ event: EventRow }>(`${manageBase(clubId)}/events`, body);
export const updateManageEvent = (clubId: string, eventId: string, body: ManageEventInput) =>
  apiPut<{ event: EventRow }>(manageEventPath(clubId, eventId), body);
export const deleteManageEvent = (clubId: string, eventId: string) =>
  apiDelete<void>(manageEventPath(clubId, eventId));
